import { fonts } from "@/constants/fonts";
import { darkTheme, lightTheme } from "@/constants/theme";
import React, { useCallback, useRef, useState } from "react";
import {
  Dimensions,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useColorScheme,
  View,
  type LayoutChangeEvent,
} from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const TAB_MIN_WIDTH = 100;
const TAB_PADDING = 20;
const TAB_GAP = 12;

export interface Tab {
  id: string;
  title: string;
  content: React.ReactNode;
  icon?: string;
}

interface SwipeableTabsProps {
  tabs: Tab[];
  onTabChange?: (tabId: string, tabIndex: number) => void;
  activeColor?: string;
  inactiveColor?: string;
  backgroundColor?: string;
}

const SwipeableTabs: React.FC<SwipeableTabsProps> = ({
  tabs,
  onTabChange,
  activeColor,
  inactiveColor,
  backgroundColor,
}) => {
  const colorScheme = useColorScheme();
  const theme = colorScheme === "dark" ? darkTheme : lightTheme;

  const [activeIndex, setActiveIndex] = useState(0);
  const [tabLayouts, setTabLayouts] = useState<{ x: number; width: number }[]>(
    []
  );
  const tabScrollViewRef = useRef<ScrollView>(null);
  const translateX = useSharedValue(0);
  const context = useSharedValue(0);
  const indicatorTranslateX = useSharedValue(0);
  const indicatorWidth = useSharedValue(TAB_MIN_WIDTH);

  const finalActiveColor = activeColor || theme.primary;
  const finalInactiveColor = inactiveColor || theme.textTertiary;
  const finalBackgroundColor = backgroundColor || theme.background;

  const shouldScroll = tabs.length > 3;

  const panGesture = Gesture.Pan()
    .activeOffsetX([-10, 10])
    .failOffsetY([-10, 10])
    .enabled(Platform.OS === "ios")
    .onStart(() => {
      "worklet";
      context.value = translateX.value;
    })
    .onUpdate((event) => {
      "worklet";
      const newTranslateX = context.value + event.translationX;
      const minTranslate = -(tabs.length - 1) * SCREEN_WIDTH;
      const maxTranslate = 0;
      translateX.value = Math.max(
        minTranslate,
        Math.min(maxTranslate, newTranslateX)
      );
    })
    .onEnd((event) => {
      "worklet";
      const threshold = SCREEN_WIDTH * 0.2;
      let newIndex = activeIndex;

      if (event.translationX < -threshold && activeIndex < tabs.length - 1) {
        newIndex = activeIndex + 1;
      } else if (event.translationX > threshold && activeIndex > 0) {
        newIndex = activeIndex - 1;
      }

      translateX.value = withSpring(-newIndex * SCREEN_WIDTH, {
        damping: 25,
        stiffness: 100,
      });

      if (newIndex !== activeIndex) {
        runOnJS(setActiveIndex)(newIndex);
      }
    });

  const scrollToTab = useCallback(
    (index: number) => {
      if (!shouldScroll || !tabScrollViewRef.current || !tabLayouts[index]) {
        return;
      }

      const tabX = tabLayouts[index].x;
      const tabWidth = tabLayouts[index].width;
      const tabCenter = tabX + tabWidth / 2;
      const screenCenter = SCREEN_WIDTH / 2;

      const scrollToX = Math.max(0, tabCenter - screenCenter);

      tabScrollViewRef.current.scrollTo({
        x: scrollToX,
        animated: true,
      });
    },
    [shouldScroll, tabLayouts]
  );

  React.useEffect(() => {
    translateX.value = withSpring(-activeIndex * SCREEN_WIDTH, {
      damping: 25,
      stiffness: 100,
    });

    if (tabLayouts[activeIndex]) {
      const layout = tabLayouts[activeIndex];
      indicatorTranslateX.value = withSpring(layout.x, {
        damping: 25,
        stiffness: 100,
      });
      indicatorWidth.value = withSpring(layout.width, {
        damping: 25,
        stiffness: 100,
      });

      scrollToTab(activeIndex);
    }

    onTabChange?.(tabs[activeIndex].id, activeIndex);
  }, [activeIndex, tabLayouts, scrollToTab]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }],
    };
  });

  const handleTabPress = (index: number) => {
    setActiveIndex(index);
  };

  const handleTabLayout = useCallback(
    (index: number, event: LayoutChangeEvent) => {
      const { x, width } = event.nativeEvent.layout;
      setTabLayouts((prev) => {
        const newLayouts = [...prev];
        newLayouts[index] = { x, width };
        return newLayouts;
      });
    },
    []
  );

  return (
    <View style={styles.container}>
      <View style={[styles.tabBar, { backgroundColor: finalBackgroundColor }]}>
        {shouldScroll ? (
          <ScrollView
            ref={tabScrollViewRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            bounces={false}
            decelerationRate="fast"
          >
            {tabs.map((tab, index) => (
              <Pressable
                key={tab.id}
                style={({ pressed }) => [
                  styles.tab,
                  styles.scrollableTab,
                  pressed && styles.tabPressed,
                ]}
                onPress={() => handleTabPress(index)}
                onLayout={(event) => handleTabLayout(index, event)}
              >
                {tab.icon && <Text style={styles.tabIcon}>{tab.icon}</Text>}
                <Text
                  style={[
                    styles.tabText,
                    { color: finalInactiveColor },
                    activeIndex === index && {
                      color: finalActiveColor,
                      fontFamily: fonts.bold,
                    },
                  ]}
                  numberOfLines={1}
                >
                  {tab.title}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        ) : (
          <View style={styles.fixedTabContainer}>
            {tabs.map((tab, index) => (
              <Pressable
                key={tab.id}
                style={({ pressed }) => [
                  styles.tab,
                  styles.fixedTab,
                  pressed && styles.tabPressed,
                ]}
                onPress={() => handleTabPress(index)}
              >
                {tab.icon && <Text style={styles.tabIcon}>{tab.icon}</Text>}
                <Text
                  style={[
                    styles.tabText,
                    { color: finalInactiveColor },
                    activeIndex === index && {
                      color: finalActiveColor,
                      fontFamily: fonts.bold,
                    },
                  ]}
                  numberOfLines={1}
                >
                  {tab.title}
                </Text>
              </Pressable>
            ))}
          </View>
        )}
      </View>

      <View style={styles.contentWrapper}>
        {Platform.OS === "android" ? (
          <View style={styles.androidContentContainer}>
            {tabs.map((tab, index) => (
              <View
                key={tab.id}
                style={[
                  styles.androidTabContent,
                  activeIndex === index
                    ? styles.androidTabContentVisible
                    : styles.androidTabContentHidden,
                ]}
                pointerEvents={activeIndex === index ? "auto" : "none"}
              >
                {tab.content}
              </View>
            ))}
          </View>
        ) : (
          <GestureDetector gesture={panGesture}>
            <Animated.View style={[styles.contentContainer, animatedStyle]}>
              {tabs.map((tab) => (
                <View key={tab.id} style={styles.tabContent}>
                  {tab.content}
                </View>
              ))}
            </Animated.View>
          </GestureDetector>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tabBar: {
    position: "relative",
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(0, 0, 0, 0.08)",
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollContent: {
    paddingHorizontal: TAB_PADDING,
    gap: TAB_GAP,
  },
  fixedTabContainer: {
    flexDirection: "row",
    paddingHorizontal: TAB_PADDING,
    paddingVertical: 12,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 6,
    borderRadius: 8,
    minHeight: 44,
  },
  scrollableTab: {
    minWidth: TAB_MIN_WIDTH,
  },
  fixedTab: {
    flex: 1,
  },
  tabPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
  tabIcon: {
    fontSize: 18,
  },
  tabText: {
    fontSize: 15,
    fontFamily: fonts.semiBold,
    letterSpacing: 0.2,
  },

  fixedIndicator: {
    left: TAB_PADDING,
  },
  contentWrapper: {
    flex: 1,
    overflow: "hidden",
  },
  contentContainer: {
    flexDirection: "row",
    height: "100%",
  },
  tabContent: {
    width: SCREEN_WIDTH,
  },
  androidContentContainer: {
    flex: 1,
  },
  androidTabContent: {
    flex: 1,
  },
  androidTabContentVisible: {
    display: "flex",
  },
  androidTabContentHidden: {
    display: "none",
  },
});

export default SwipeableTabs;
